import axios from 'axios';
import type { FnolRequiredClaimAttributesConfig } from '@dispatch/shared/types';
import { v4 as uuidv4 } from 'uuid';
import { dispatchClient } from '../clients';

const APIM_SERVICE_CONFIGURATION_URL = (tenantId: string, key: string) =>
  `configuration/${tenantId}/${key}`;

const FNOL_TTL_FEATURE_KEY = 'feature:fnol-ttl-card';

export const CLAIM_OWNER_CONFIG_KEY = 'claimowner';

export const FNOL_REQUIRED_CLAIM_ATTRIBUTES_KEY =
  'fnol-required-claim-attributes';

export interface ClaimOwnerConfig {
  fnolCacheTtlSeconds?: number;
  [key: string]: unknown;
}

export async function getConfiguration<T>(
  tenantId: string,
  key: string
): Promise<T> {
  const response = await dispatchClient.get<T>(
    APIM_SERVICE_CONFIGURATION_URL(tenantId, key),
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  return response?.data;
}

export async function putConfiguration<T>(
  tenantId: string,
  key: string,
  value: T
): Promise<void> {
  await dispatchClient.put(
    APIM_SERVICE_CONFIGURATION_URL(tenantId, key),
    value,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );
}

export async function getClaimOwnerConfig(
  tenantId: string
): Promise<ClaimOwnerConfig> {
  return getConfiguration<ClaimOwnerConfig>(tenantId, CLAIM_OWNER_CONFIG_KEY);
}

export async function putClaimOwnerConfig(
  tenantId: string,
  value: ClaimOwnerConfig
): Promise<void> {
  return putConfiguration<ClaimOwnerConfig>(
    tenantId,
    CLAIM_OWNER_CONFIG_KEY,
    value
  );
}

export async function getFnolTtlCardFeature(
  tenantId: string
): Promise<boolean> {
  try {
    const response = await dispatchClient.get<unknown>(
      APIM_SERVICE_CONFIGURATION_URL(tenantId, FNOL_TTL_FEATURE_KEY),
      {
        headers: {
          'Request-ID': uuidv4(),
        },
      }
    );

    const value = response?.data;

    if (value === undefined || value === null) {
      return false;
    }

    return String(value).toLowerCase() === 'true';
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error fetching fnol-ttl-card feature toggle from API', e);
    return false;
  }
}

const toAttributeNames = (value: unknown): string[] => {
  if (typeof value !== 'object' || value === null) {
    return [];
  }

  // The API value is untyped JSON; attributeNames is narrowed below.
  const { attributeNames } = value as { attributeNames?: unknown };
  if (!Array.isArray(attributeNames)) {
    return [];
  }

  return attributeNames.filter(
    (name): name is string => typeof name === 'string' && name.trim() !== ''
  );
};

/**
 * Reads the tenant's list of claim attribute display names that the qualified
 * FNOL flow must collect. A missing key (404) or an unusable value means none.
 */
export async function getFnolRequiredClaimAttributes(
  tenantId: string
): Promise<FnolRequiredClaimAttributesConfig> {
  try {
    const value = await getConfiguration<unknown>(
      tenantId,
      FNOL_REQUIRED_CLAIM_ATTRIBUTES_KEY
    );

    return { attributeNames: toAttributeNames(value) };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { attributeNames: [] };
    }
    throw error;
  }
}
